import React, { useEffect, useState, useCallback } from "react";
import { Card, Descriptions, InputNumber, Button, Space, message, Popconfirm, Spin, Typography, Progress, Select, Tag } from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import { useIsMobile } from "../../../hooks/useIsMobile";
import config from "../../../api_config.json";

const LIMIT_KEYS = {
  MAX_CLOUD_STORAGE_BYTES: "max_cloud_storage_bytes",
  MAX_COMMUNICATION_TEMPLATES: "max_communication_templates",
  MAX_CUSTOM_DOCUMENTS: "max_custom_documents",
  MAX_OBITUARIES: "max_obituaries",
  MAX_SMTP_EXTERNAL_COMMUNICATIONS: "max_smtp_external_communications",
};

const MANAGEMENT_MODES = {
  AUTOMATIC: "automatic",
  MANUAL: "manual",
};

const AUTOMATION_SOURCES = {
  STRIPE_QUOTE: "stripe_quote",
};

const AUTOMATION_SOURCE_LABELS = {
  [AUTOMATION_SOURCES.STRIPE_QUOTE]: "Stripe quote",
};

const BYTES_IN_GB = 1024 ** 3;
const TEMPLATE_CHANNEL_SUFFIX_REGEX = /\s*(WhatsApp|SMS|Telegram|Email)\s*$/i;

const LIMIT_LABELS = {
  [LIMIT_KEYS.MAX_CLOUD_STORAGE_BYTES]: "Max Spazio Cloud",
  [LIMIT_KEYS.MAX_COMMUNICATION_TEMPLATES]: "Max Modelli",
  [LIMIT_KEYS.MAX_CUSTOM_DOCUMENTS]: "Max Documenti Personalizzati",
  [LIMIT_KEYS.MAX_OBITUARIES]: "Max Necrologi",
  [LIMIT_KEYS.MAX_SMTP_EXTERNAL_COMMUNICATIONS]: "Max Invii SMTP Anno",
};

const LIMIT_GROUPS = [
  {
    title: "Archiviazione Dati",
    keys: [LIMIT_KEYS.MAX_CLOUD_STORAGE_BYTES],
  },
  {
    title: "Comunicazioni Esterne",
    keys: [LIMIT_KEYS.MAX_COMMUNICATION_TEMPLATES, LIMIT_KEYS.MAX_SMTP_EXTERNAL_COMMUNICATIONS],
  },
  {
    title: "Documenti Personalizzati",
    keys: [LIMIT_KEYS.MAX_CUSTOM_DOCUMENTS],
  },
  {
    title: "Vendite e Marketing",
    keys: [LIMIT_KEYS.MAX_OBITUARIES],
  },
];

const isCloudStorageLimit = (key) => key === LIMIT_KEYS.MAX_CLOUD_STORAGE_BYTES;

const hasUsageProgress = (key) => Boolean(LIMIT_LABELS[key]);

const bytesToGb = (bytes) => Number(bytes || 0) / BYTES_IN_GB;

const gbToBytes = (gb) => Math.round(Number(gb || 0) * BYTES_IN_GB);

const formatGb = (gb) =>
  Number(gb || 0).toLocaleString("it-IT", {
    maximumFractionDigits: 2,
  });

const formatLimitAmount = (key, amount) => {
  if (amount === null || amount === undefined) return "-";
  if (isCloudStorageLimit(key)) return `${formatGb(bytesToGb(amount))} GB`;
  return amount;
};

const formatBytes = (bytes) => {
  const safeBytes = Number(bytes || 0);
  if (!safeBytes) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(Math.floor(Math.log(safeBytes) / Math.log(1024)), units.length - 1);
  const value = safeBytes / 1024 ** unitIndex;

  return `${value.toLocaleString("it-IT", { maximumFractionDigits: unitIndex === 0 ? 0 : 2 })} ${units[unitIndex]}`;
};

const formatUsageAmount = (key, amount) => {
  if (isCloudStorageLimit(key)) return formatBytes(amount);
  return Number(amount || 0).toLocaleString("it-IT");
};

const getEditValue = (key, amount) => {
  if (amount === null || amount === undefined) return null;
  if (isCloudStorageLimit(key)) return bytesToGb(amount);
  return amount;
};

const getPersistedValue = (key, value) => {
  if (isCloudStorageLimit(key)) return gbToBytes(value);
  return value;
};

const getManagementMode = (limit) =>
  limit?.management_mode === MANAGEMENT_MODES.MANUAL
    ? MANAGEMENT_MODES.MANUAL
    : MANAGEMENT_MODES.AUTOMATIC;

const getAutomationSource = (limit) =>
  limit?.automation_source || AUTOMATION_SOURCES.STRIPE_QUOTE;

const getAutomationSourceLabel = (source) =>
  AUTOMATION_SOURCE_LABELS[source] || source || "Automazione";

const buildManagementFields = (managementMode) => {
  if (managementMode === MANAGEMENT_MODES.MANUAL) {
    return {
      management_mode: MANAGEMENT_MODES.MANUAL,
      automation_source: null,
      manual_reason: "mission_control",
    };
  }

  return {
    management_mode: MANAGEMENT_MODES.AUTOMATIC,
    automation_source: AUTOMATION_SOURCES.STRIPE_QUOTE,
    manual_reason: null,
  };
};

const normalizeTemplateFamilyDisplayName = (name) => {
  const trimmedName = String(name || "").trim();
  const familyName = trimmedName.replace(TEMPLATE_CHANNEL_SUFFIX_REGEX, "").trim();
  return familyName || trimmedName;
};

const countTemplateFamilies = (templates) => {
  const familyKeys = new Set();

  for (const template of templates) {
    const relatedClass = template?.related_class || "";
    const familyName = normalizeTemplateFamilyDisplayName(template?.name).toLocaleLowerCase();
    familyKeys.add(`${relatedClass}::${familyName}`);
  }

  return familyKeys.size;
};

const getCurrentYearBounds = () => {
  const year = new Date().getFullYear();
  return {
    startOfYear: `${year}-01-01T00:00:00.000Z`,
    endOfYear: `${year}-12-31T23:59:59.999Z`,
  };
};

const getUsageColor = (percent) => {
  if (percent >= 100) return "#cf1322";
  if (percent >= 80) return "#faad14";
  return "#52c41a";
};

const fetchOrgLimitsQuery = `
  query FetchOrgLimits($orgId: ID!) {
    org_limits(
      where: { org_pointer: { have: { objectId: { equalTo: $orgId } } } }
      first: 100
    ) {
      edges {
        node {
          objectId
          namespace
          amount
          management_mode
          automation_source
          manual_reason
        }
      }
    }
  }
`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const fetchOrgLimitUsageQuery = `
  query FetchOrgLimitUsage($orgId: ID!, $startOfYear: Date!, $endOfYear: Date!) {
    org_storages(
      first: 1
      where: { org_pointer: { have: { objectId: { equalTo: $orgId } } } }
      order: [updatedAt_DESC]
    ) {
      edges {
        node {
          total_bytes
        }
      }
    }
    outbound_templates(
      first: 1000
      where: { org_pointer: { have: { objectId: { equalTo: $orgId } } } }
    ) {
      edges {
        node {
          objectId
          name
          related_class
        }
      }
    }
    org_docs(
      first: 0
      where: { org_pointer: { have: { objectId: { equalTo: $orgId } } } }
    ) {
      count
    }
    annunci_funebris(
      first: 0
      where: {
        AND: [
          { org_pointer: { have: { objectId: { equalTo: $orgId } } } }
          { createdAt: { greaterThanOrEqualTo: $startOfYear } }
          { createdAt: { lessThanOrEqualTo: $endOfYear } }
        ]
      }
    ) {
      count
    }
    outbound_sents(
      first: 0
      where: {
        AND: [
          { org_pointer: { have: { objectId: { equalTo: $orgId } } } }
          { type: { equalTo: "email_smtp" } }
          { status: { notIn: ["draft", "scheduled"] } }
          { createdAt: { greaterThanOrEqualTo: $startOfYear } }
          { createdAt: { lessThanOrEqualTo: $endOfYear } }
        ]
      }
    ) {
      count
    }
  }
`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const createOrgLimitMutation = `
  mutation CreateOrgLimit($fields: CreateOrg_limitFieldsInput) {
    createOrg_limit(input: { fields: $fields }) {
      org_limit {
        objectId
        namespace
        amount
        management_mode
        automation_source
        manual_reason
      }
    }
  }
`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const updateOrgLimitMutation = `
  mutation UpdateOrgLimit($id: ID!, $fields: UpdateOrg_limitFieldsInput) {
    updateOrg_limit(input: { id: $id, fields: $fields }) {
      org_limit {
        objectId
        namespace
        amount
        management_mode
        automation_source
        manual_reason
      }
    }
  }
`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const OrgLimits = ({ org }) => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const isMobile = useIsMobile();
  const graphqlUrl = `${config.baseUrl[isProd ? "prod" : "dev"]}/graphql?raw=true`;

  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState({}); // { namespace: { objectId, amount } }
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(null);
  const [usage, setUsage] = useState({
    [LIMIT_KEYS.MAX_CLOUD_STORAGE_BYTES]: 0,
    [LIMIT_KEYS.MAX_COMMUNICATION_TEMPLATES]: 0,
    [LIMIT_KEYS.MAX_CUSTOM_DOCUMENTS]: 0,
    [LIMIT_KEYS.MAX_OBITUARIES]: 0,
    [LIMIT_KEYS.MAX_SMTP_EXTERNAL_COMMUNICATIONS]: 0,
  });
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState(null);
  const [editManagementMode, setEditManagementMode] = useState(MANAGEMENT_MODES.MANUAL);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchLimits = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getTokenSilently();
      const response = await fetch(graphqlUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: fetchOrgLimitsQuery,
          variables: { orgId: org.objectId },
        }),
      });
      const json = await response.json();
      if (json.errors) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }

      const limitsMap = {};
      // Handle both wrapped (json.data.org_limits) and unwrapped (json.org_limits) responses
      const edges = json.data?.org_limits?.edges || json.org_limits?.edges || [];
      for (const edge of edges) {
        const node = edge?.node;
        if (node?.namespace) {
          limitsMap[node.namespace] = {
            objectId: node.objectId,
            amount: node.amount,
            management_mode: node.management_mode,
            automation_source: node.automation_source,
            manual_reason: node.manual_reason,
          };
        }
      }
      setLimits(limitsMap);
    } catch (error) {
      console.error("Error fetching org limits:", error);
      message.error("Errore nel caricamento dei limiti: " + error.message);
    } finally {
      setLoading(false);
    }
  }, [getTokenSilently, graphqlUrl, org.objectId]);

  const fetchUsage = useCallback(async () => {
    try {
      setUsageLoading(true);
      setUsageError(null);
      const token = await getTokenSilently();
      const { startOfYear, endOfYear } = getCurrentYearBounds();
      const response = await fetch(graphqlUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: fetchOrgLimitUsageQuery,
          variables: {
            orgId: org.objectId,
            startOfYear,
            endOfYear,
          },
        }),
      });
      const json = await response.json();
      if (json.errors) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }

      const data = json.data || json;
      const storageBytes = Number(data?.org_storages?.edges?.[0]?.node?.total_bytes || 0);
      const templates = (data?.outbound_templates?.edges || [])
        .map((edge) => edge?.node)
        .filter(Boolean);

      setUsage({
        [LIMIT_KEYS.MAX_CLOUD_STORAGE_BYTES]: storageBytes,
        [LIMIT_KEYS.MAX_COMMUNICATION_TEMPLATES]: countTemplateFamilies(templates),
        [LIMIT_KEYS.MAX_CUSTOM_DOCUMENTS]: Number(data?.org_docs?.count || 0),
        [LIMIT_KEYS.MAX_OBITUARIES]: Number(data?.annunci_funebris?.count || 0),
        [LIMIT_KEYS.MAX_SMTP_EXTERNAL_COMMUNICATIONS]: Number(data?.outbound_sents?.count || 0),
      });
    } catch (error) {
      console.error("Error fetching org limit usage:", error);
      setUsageError(error.message || "Errore nel caricamento dell'utilizzo");
    } finally {
      setUsageLoading(false);
    }
  }, [getTokenSilently, graphqlUrl, org.objectId]);

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const handleEdit = (key) => {
    setEditingKey(key);
    setEditValue(getEditValue(key, limits[key]?.amount));
    setEditManagementMode(getManagementMode(limits[key]));
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue(null);
    setEditManagementMode(MANAGEMENT_MODES.MANUAL);
    setConfirmOpen(false);
  };

  const handleSave = async () => {
    if (editingKey === null) return;

    try {
      setSaving(true);
      const token = await getTokenSilently();
      const existingLimit = limits[editingKey];

      let mutation;
      let variables;

      if (existingLimit?.objectId) {
        // Update existing
        mutation = updateOrgLimitMutation;
        variables = {
          id: existingLimit.objectId,
          fields: {
            amount: getPersistedValue(editingKey, editValue),
            ...buildManagementFields(editManagementMode),
          },
        };
      } else {
        // Create new
        mutation = createOrgLimitMutation;
        variables = {
          fields: {
            namespace: editingKey,
            amount: getPersistedValue(editingKey, editValue),
            org_pointer: { link: org.objectId },
            ...buildManagementFields(editManagementMode),
          },
        };
      }

      const response = await fetch(graphqlUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query: mutation, variables }),
      });

      const json = await response.json();
      if (json.errors) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }

      message.success("Limite aggiornato");
      setEditingKey(null);
      setEditValue(null);
      setEditManagementMode(MANAGEMENT_MODES.MANUAL);
      setConfirmOpen(false);
      fetchLimits();
    } catch (error) {
      console.error("Error saving org limit:", error);
      message.error("Errore nel salvataggio: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const renderManagementTag = (limit) => {
    const managementMode = getManagementMode(limit);

    if (managementMode === MANAGEMENT_MODES.MANUAL) {
      return <Tag color="gold">Manuale</Tag>;
    }

    return (
      <Tag color="blue">
        Automatico: {getAutomationSourceLabel(getAutomationSource(limit))}
      </Tag>
    );
  };

  const renderLimitField = (key) => {
    const limit = limits[key];
    const isEditing = editingKey === key;
    const currentValue = limit?.amount;
    const displayValue = formatLimitAmount(key, currentValue);

    if (isEditing) {
      const unitLabel = isCloudStorageLimit(key) ? "GB" : null;

      return (
        <Space wrap>
          <InputNumber
            min={0}
            precision={isCloudStorageLimit(key) ? 2 : 0}
            value={editValue}
            onChange={(v) => setEditValue(v)}
            style={{ width: 120 }}
          />
          {unitLabel ? <Typography.Text type="secondary">{unitLabel}</Typography.Text> : null}
          <Select
            value={editManagementMode}
            onChange={setEditManagementMode}
            style={{ width: 170 }}
            options={[
              { value: MANAGEMENT_MODES.MANUAL, label: "Manuale" },
              {
                value: MANAGEMENT_MODES.AUTOMATIC,
                label: "Automatico: Stripe quote",
              },
            ]}
          />
          <Popconfirm
            title="Confermi la modifica del limite?"
            open={confirmOpen}
            okButtonProps={{ loading: saving }}
            onConfirm={handleSave}
            onCancel={() => setConfirmOpen(false)}
          >
            <Button
              size="small"
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => setConfirmOpen(true)}
            >
              Salva
            </Button>
          </Popconfirm>
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={handleCancel}
          >
            Annulla
          </Button>
        </Space>
      );
    }

    return (
      <Space wrap>
        <span>{displayValue}</span>
        {renderManagementTag(limit)}
        <Button
          size="small"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(key)}
        >
          Modifica
        </Button>
      </Space>
    );
  };

  const renderUsageProgress = (key) => {
    if (!hasUsageProgress(key)) return null;

    if (usageLoading) {
      return (
        <div style={{ minWidth: isMobile ? "100%" : 220, textAlign: "left" }}>
          <Spin size="small" />
        </div>
      );
    }

    if (usageError) {
      return (
        <Typography.Text type="secondary" style={{ minWidth: isMobile ? "100%" : 220, textAlign: "left" }}>
          Uso non disponibile
        </Typography.Text>
      );
    }

    const limitAmount = Number(limits[key]?.amount || 0);
    const currentUsage = Number(usage[key] || 0);

    if (limitAmount <= 0) {
      return (
        <Typography.Text type="secondary" style={{ minWidth: isMobile ? "100%" : 220, textAlign: "left" }}>
          Nessun limite configurato
        </Typography.Text>
      );
    }

    const actualPercent = (currentUsage / limitAmount) * 100;
    const progressPercent = Math.min(Math.round(actualPercent), 100);
    const roundedActualPercent = Math.round(actualPercent);

    return (
      <div style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Utilizzo
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatUsageAmount(key, currentUsage)} / {formatLimitAmount(key, limitAmount)}
          </Typography.Text>
        </div>
        <Progress
          percent={progressPercent}
          showInfo={false}
          strokeColor={getUsageColor(actualPercent)}
          status={actualPercent >= 100 ? "exception" : "normal"}
          size="small"
          trailColor="#f0f0f0"
        />
        <Typography.Text type={actualPercent >= 100 ? "danger" : "secondary"} style={{ display: "block", fontSize: 12, textAlign: "right" }}>
          {roundedActualPercent}%
        </Typography.Text>
      </div>
    );
  };

  const renderLimitContent = (key) => {
    const usageProgress = renderUsageProgress(key);

    return (
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          maxWidth: isMobile ? "100%" : 420,
        }}
      >
        <Typography.Text strong style={{ display: "block", marginBottom: 6 }}>
          {LIMIT_LABELS[key]}
        </Typography.Text>
        {usageProgress}
        <div
          style={{
            alignSelf: "flex-start",
          }}
        >
          {renderLimitField(key)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card title="Limiti" bordered={false}>
        <Spin />
      </Card>
    );
  }

  return (
    <Card title="Limiti" bordered={false}>
      {LIMIT_GROUPS.map((group, index) => (
        <div key={group.title} style={{ marginBottom: index < LIMIT_GROUPS.length - 1 ? 16 : 0 }}>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            {group.title}
          </Typography.Text>
          <Descriptions column={1} size="small" layout={isMobile ? "vertical" : "horizontal"} colon={false}>
            {group.keys.map((key) => (
              <Descriptions.Item key={key} contentStyle={{ width: "100%" }}>
                <Card size="small" bordered style={{ width: "100%" }}>
                  {renderLimitContent(key)}
                </Card>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      ))}
    </Card>
  );
};

export default OrgLimits;
